import React, {createContext, useContext, useEffect, useState} from "react";
import {auth} from "../firebase";

import {
    onAuthStateChanged,
    sendEmailVerification,
    signInWithEmailAndPassword,
    createUserWithEmailAndPassword,
    sendPasswordResetEmail,
    signInWithRedirect,
    signOut
} from "firebase/auth";

import Loading from "../components/Loading";
import {connect} from "react-redux";
import PropTypes from "prop-types";
import {LOGOUT_REQUESTED, REFRESH_TOKEN} from "../redux/actions/auth-actions";
import {useNavigate} from "react-router-dom";
import {toast} from "react-toastify";
import ToastMessage from "../components/elements/toast";

// create context
const AuthContext = createContext();

// use AuthContext
export const useAuth = () => {
    return useContext(AuthContext);
};

// AuthContext Provider with values
const AuthProvider = ({ children, setToken, logOut}) => {


    const [currentUser, setCurrentUser] = useState(null);
    const [timeActive, setTimeActive] = useState(false)
    const [loading, setLoading] = useState(true);

    const navigate = useNavigate()

    if(currentUser){
        setToken(currentUser.accessToken)
    }

    useEffect(() => {
        // onAuthStateChanged will executed in login and logout
        // unsubscribe when unmounting the component
        return onAuthStateChanged(auth, async (user) => {
            setCurrentUser(user);
            setLoading(false); // set loading to false to after loading done
        });
    }, []);

    // google sign up with popup
    let doSocialSignIn = (provider) => {
        setLoading(true)
        return signInWithRedirect(auth, provider);
    };

    // this will logout a user
    let doLogout = () => {
        logOut()
        return signOut(auth);
    };

    let doSignupWithEmailPass = async (email, password) => {
        try {
            toast.dismiss()
            const response = await createUserWithEmailAndPassword(auth, email, password)
            if(response){
                try {
                    await sendEmailVerification(auth.currentUser)
                    setTimeActive(true)
                    navigate('/verify-email')
                } catch (error) {
                    toast.error(<ToastMessage text={error.message} withSupportButton={true} />)
                    return false
                }
            }
        } catch (error) {
            switch (error.code) {
                case 'auth/invalid-email':
                    toast.error(<ToastMessage text={"Invalid email."} withTryAgain={true} />,
                        {autoClose: false}
                    )
                    break;
                default:
                    toast.error(
                        <ToastMessage text={`An error occurred ${error.code}.`} withSupportButton={true} />,
                        {autoClose: false}
                    )
                    break;
            }
            return false
        }
        return true
    };

    let doSendEmailVerification = () => {
        sendEmailVerification(auth.currentUser)
            .then(() => {
                setTimeActive(true)
            }).catch((error) => {
                toast.error(<ToastMessage text={error.message} withSupportButton={true} />,
                    {autoClose: false}
                )
            })
    }

    // this will login with email and pasword
    let doSigninWithEmailPass = (email, password) => {
        return signInWithEmailAndPassword(auth, email, password);
    };

    // this will reset user password
    let doResetPassword = (email) => {
        return sendPasswordResetEmail(auth, email)
    };

    // this will remove current user
    let doRemoveUser = () => {
        return currentUser.delete();
    };

    // context value object
    const value = {
        currentUser,
        loading,
        setLoading,
        timeActive,
        setTimeActive,
        doSocialSignIn,
        doLogout,
        doSignupWithEmailPass,
        doSendEmailVerification,
        doSigninWithEmailPass,
        doResetPassword,
        doRemoveUser,
    };

    return (
        <AuthContext.Provider value={value}>
            {!loading ? children : (
                <div className="flex flex-col justify-center items-center bg-brandYellow min-h-screen">
                    <Loading size="lg" />
                </div>
            )}
        </AuthContext.Provider>
    );
};

AuthProvider.propTypes = {
    setToken: PropTypes.func,
}

const mapStateToProps = (state) => ({
})

const mapDispatchToProps = (dispatch) => ({
    setToken: (token) => dispatch({type: REFRESH_TOKEN, payload: token}),
    logOut: () => dispatch({ type: LOGOUT_REQUESTED }),
})

export default connect(mapStateToProps, mapDispatchToProps)(AuthProvider)
